import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, X } from "lucide-react";
import { fmtDT } from "../lib/date";
import { Dialog, DialogClose, DialogContent, DialogTitle } from "./ui/dialog";

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.25;

/**
 * 付款验收照片全屏预览：在同一笔付款的阶段照片间翻页，并放大查看水印细节。
 * 用现有付款记录和完工资料展示地址、阶段、拍摄 / 上传时间与验收人，避免只看原图时丢失凭证上下文。
 */
export default function PaymentAcceptanceLightbox({ items, index, request, fileUrl, zh, onIndex, onClose }) {
  const touchStartX = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [viewport, setViewport] = useState({ width: 0, height: 0 });
  const [naturalSize, setNaturalSize] = useState(null);
  const current = items[index];
  const currentId = current?.id || current?.fileId;
  const multiple = items.length > 1;

  const stepPhoto = useCallback((delta) => {
    if (multiple) onIndex((index + delta + items.length) % items.length);
  }, [index, items.length, multiple, onIndex]);
  const changeZoom = useCallback((delta) => setZoom((value) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round((value + delta) * 100) / 100))), []);
  const captureImageSize = useCallback((image) => {
    if (!image?.naturalWidth || !image.naturalHeight) return;
    setNaturalSize((previous) => previous?.id === currentId && previous.width === image.naturalWidth && previous.height === image.naturalHeight
      ? previous
      : { id: currentId, width: image.naturalWidth, height: image.naturalHeight });
  }, [currentId]);
  const attachImage = useCallback((image) => {
    if (!image) return undefined;
    let active = true;
    const measure = () => { if (active) captureImageSize(image); };
    measure();
    image.addEventListener("load", measure);
    if (image.decode) image.decode().then(measure).catch(() => {});
    return () => { active = false; image.removeEventListener("load", measure); };
  }, [captureImageSize]);
  const attachViewport = useCallback((element) => {
    if (!element) return undefined;
    const measure = () => setViewport((previous) => previous.width === element.clientWidth && previous.height === element.clientHeight
      ? previous
      : { width: element.clientWidth, height: element.clientHeight });
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setZoom(1);
  }, [currentId]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.key === "ArrowLeft" && multiple) { event.preventDefault(); stepPhoto(-1); }
      else if (event.key === "ArrowRight" && multiple) { event.preventDefault(); stepPhoto(1); }
      else if (event.key === "+" || event.key === "=") { event.preventDefault(); changeZoom(ZOOM_STEP); }
      else if (event.key === "-") { event.preventDefault(); changeZoom(-ZOOM_STEP); }
      else if (event.key === "0") { event.preventDefault(); setZoom(1); }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [changeZoom, index, items.length, multiple, stepPhoto]);

  if (!current) return null;

  // Dialog 门户可能在 effect 之后才挂载；两个 ref 分别读取可用空间和图片尺寸。
  const imageNaturalSize = naturalSize?.id === currentId ? naturalSize : null;
  // 默认留出边距并保持原图比例；小图不放大，放大按钮仍可查看细节。
  const fitScale = imageNaturalSize && viewport.width && viewport.height
    ? Math.min(1, Math.max(1, viewport.width - 32) * 0.9 / imageNaturalSize.width, Math.max(1, viewport.height - 32) * 0.85 / imageNaturalSize.height)
    : 0;
  const imageWidth = fitScale ? Math.max(1, imageNaturalSize.width * fitScale * zoom) : viewport.width;
  const imageHeight = fitScale ? Math.max(1, imageNaturalSize.height * fitScale * zoom) : viewport.height;
  const innerWidth = Math.max(viewport.width, imageWidth);
  const innerHeight = Math.max(viewport.height, imageHeight);
  const imageUrl = fileUrl(current.wmFileId || current.fileId);
  const stage = current.milestoneName || request.milestoneName || "—";
  const accepted = request.acceptance || {};
  const stepButton = "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-slate-800/90 text-white hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 disabled:cursor-not-allowed disabled:opacity-40";

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        data-testid="pay-lightbox"
        closeLabel={zh ? "关闭照片预览" : "Close photo preview"}
        overlayClassName="bg-slate-950/95 backdrop-blur-sm"
        className="!fixed !left-0 !top-0 !translate-x-0 !translate-y-0 flex h-[100dvh] max-h-[100dvh] w-screen max-w-none flex-col gap-0 overflow-hidden rounded-none border-0 bg-slate-950 p-0 text-white shadow-none [&_[data-dialog-close]]:hidden"
      >
        <DialogTitle className="sr-only">{zh ? "付款验收照片" : "Payment acceptance photos"}</DialogTitle>
        <div className="flex h-full min-h-0 flex-col" onClick={(event) => event.stopPropagation()}>
          <header className="relative z-10 flex min-h-14 flex-wrap items-center justify-between gap-2 border-b border-white/10 bg-slate-950/90 px-3 py-2 sm:px-5">
            <span className="inline-flex max-w-full items-center rounded-md bg-emerald-700/90 px-3 py-1.5 text-xs font-semibold text-white sm:text-sm">
              {zh ? "ALIGCUS · 验收凭证 Acceptance record" : "ALIGCUS · Acceptance record"}
            </span>
            <div className="flex flex-wrap items-center gap-2">
              {multiple && <span data-testid="pay-lightbox-counter" aria-live="polite" className="min-w-16 text-center text-xs tabular-nums text-slate-200">{index + 1} / {items.length}</span>}
              <button type="button" data-testid="pay-lightbox-zoom-out" aria-label={zh ? "缩小照片" : "Zoom out"} title={zh ? "缩小" : "Zoom out"} disabled={zoom <= MIN_ZOOM} onClick={() => changeZoom(-ZOOM_STEP)} className={stepButton}>
                <ZoomOut className="h-5 w-5" aria-hidden="true" />
              </button>
              <button type="button" data-testid="pay-lightbox-zoom-reset" aria-label={zh ? "恢复 100% 缩放" : "Reset zoom to 100%"} title={zh ? "恢复 100%" : "Reset to 100%"} onClick={() => setZoom(1)} className="h-11 min-w-14 rounded-full px-2 text-xs font-semibold tabular-nums text-white hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400">
                {Math.round(zoom * 100)}%
              </button>
              <button type="button" data-testid="pay-lightbox-zoom-in" aria-label={zh ? "放大照片" : "Zoom in"} title={zh ? "放大" : "Zoom in"} disabled={zoom >= MAX_ZOOM} onClick={() => changeZoom(ZOOM_STEP)} className={stepButton}>
                <ZoomIn className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
            <DialogClose asChild>
              <button type="button" data-testid="pay-lightbox-close" aria-label={zh ? "关闭照片预览" : "Close photo preview"} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-slate-800 text-white hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400">
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </DialogClose>
          </header>

          <div
            ref={attachViewport}
            data-testid="pay-lightbox-image-viewport"
            className="relative min-h-0 flex-1 overflow-auto overscroll-contain"
            onTouchStart={(event) => { touchStartX.current = event.touches[0]?.clientX ?? null; }}
            onTouchEnd={(event) => {
              if (touchStartX.current === null) return;
              const startX = touchStartX.current;
              touchStartX.current = null;
              if (!multiple || zoom !== 1) return;
              const delta = event.changedTouches[0].clientX - startX;
              if (Math.abs(delta) > 48) stepPhoto(delta > 0 ? -1 : 1);
            }}
          >
            {multiple && (
              <button type="button" data-testid="pay-lightbox-prev" aria-label={zh ? "上一张照片" : "Previous photo"} onClick={() => stepPhoto(-1)} className={`absolute left-2 top-1/2 z-10 -translate-y-1/2 ${stepButton}`}>
                <ChevronLeft className="h-6 w-6" aria-hidden="true" />
              </button>
            )}
            <div className="grid min-h-full min-w-full place-items-center" style={{ width: innerWidth, height: innerHeight }}>
              <img
                ref={attachImage}
                key={current.id || current.fileId}
                src={imageUrl}
                alt={current.filename || (zh ? "验收照片" : "Acceptance photo")}
                data-testid="pay-lightbox-image"
                draggable={false}
                onLoad={(event) => captureImageSize(event.currentTarget)}
                className="block max-h-full max-w-full select-none object-contain"
                style={{ width: imageWidth || "100%", height: imageHeight || "100%" }}
              />
            </div>
            {multiple && (
              <button type="button" data-testid="pay-lightbox-next" aria-label={zh ? "下一张照片" : "Next photo"} onClick={() => stepPhoto(1)} className={`absolute right-2 top-1/2 z-10 -translate-y-1/2 ${stepButton}`}>
                <ChevronRight className="h-6 w-6" aria-hidden="true" />
              </button>
            )}
          </div>

          <footer className="shrink-0 border-t border-white/10 bg-slate-950/90 px-4 py-3 sm:px-6">
            <div className="mx-auto max-w-7xl space-y-0.5 text-xs leading-5 sm:text-sm">
              <p className="truncate font-semibold text-white" title={request.address || ""}>{request.address || "—"}</p>
              <p className="truncate text-slate-200" title={`${stage}${request.tradeName ? ` · ${request.tradeName}` : ""}`}>
                {zh ? "阶段 Stage" : "Stage"} · {stage}{request.tradeName ? ` · ${request.tradeName}` : ""}
              </p>
              <p className="text-slate-300">
                {zh ? "拍摄 Taken" : "Taken"} {fmtDT(current.takenAt, "—")} · {zh ? "上传 Uploaded" : "Uploaded"} {fmtDT(current.at, "—")}
              </p>
              <p className="text-slate-300">
                {accepted.by
                  ? <>{zh ? "验收合格 Accepted" : "Accepted"} · {accepted.by}{accepted.at ? ` · ${fmtDT(accepted.at)}` : ""}{accepted.note ? ` · ${accepted.note}` : ""}</>
                  : (zh ? "等待验收" : "Awaiting acceptance")}
              </p>
            </div>
          </footer>
        </div>
      </DialogContent>
    </Dialog>
  );
}
